import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { Tick } from '../components/AppIcons';
import { Brand } from '../../components/Brand';
import { useActiveCompany } from '../state';
import { SECTORS } from '../data';
import {
  COMPANY_TYPES,
  getCompanyConfig,
  isRegisteredCompanyType,
  previewCompanyQuestions,
  updateCompany,
  type CompanyConfigDto,
  type CompanyRegion,
  type CompanyType,
  type OnboardingQuestionWithValue,
} from '../../lib/entities/companies';
import { questionDepth, type EntityQuestionValue } from '../../lib/entities/entityQuestions';
import '../../styles/app.css';
import '../../styles/app-company.css';

/**
 * The company configuration page (/app/company): every value that shapes what
 * Reeve expects of this business, editable in one place. Reachable from the
 * sidebar company chip and the "Company" nav item.
 *
 * Effective headcount = max(declared, employees) is the number Reeve uses for
 * legal scope, so this page makes the three numbers — and which one is used —
 * unmistakable. Saving persists via PUT /api/companies/{id} then refetches the
 * portfolio so the chip's count updates.
 */

const REGION_OPTIONS: { value: CompanyRegion; label: string }[] = [
  { value: 'england-wales', label: 'England & Wales' },
  { value: 'scotland', label: 'Scotland' },
];

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Show a flag for a moment, then clear it — the optimistic "Saved" toast. */
function useTransientFlag(ms = 2800): [boolean, () => void] {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!on) return;
    const t = setTimeout(() => setOn(false), ms);
    return () => clearTimeout(t);
  }, [on, ms]);
  return [on, () => setOn(true)];
}

export default function CompanyPage() {
  const { companyId, company, loading, companyLoading, refetchCompanies } = useActiveCompany();

  // Config load hangs off the active company. Kept separate from the display
  // Company so this page owns the full editable shape (findings, both counts).
  const [config, setConfig] = useState<CompanyConfigDto | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Editable fields (seeded from the fetched config).
  const [name, setName] = useState('');
  const [sector, setSector] = useState('');
  const [region, setRegion] = useState<CompanyRegion>('england-wales');
  const [declared, setDeclared] = useState('0');
  const [companyType, setCompanyType] = useState<CompanyType>('soleTrader');
  const [companyNumber, setCompanyNumber] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // The employer predicate questions, and the yes/no answers being edited. The LIST is server-owned:
  // toggling one re-asks the backend which questions now apply, so progressive disclosure is decided in
  // exactly one place (COMPLIANCE-MODEL.md §10) and a question hanging off a DERIVED parent, which the
  // browser cannot evaluate, still appears.
  const [predicateQuestions, setPredicateQuestions] = useState<EntityQuestionValue[]>([]);
  const [predicates, setPredicates] = useState<Record<string, boolean>>({});

  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, showSaved] = useTransientFlag();

  // Synchronous re-entry lock: state alone can't stop a fast double-click.
  const submitLock = useRef(false);

  /** Reseed every editable field from a fresh config. */
  function applyConfig(cfg: CompanyConfigDto) {
    setConfig(cfg);
    setName(cfg.name);
    setSector(cfg.sector);
    setRegion(cfg.region);
    setDeclared(String(cfg.declaredHeadcount));
    setCompanyType(cfg.companyType);
    setCompanyNumber(cfg.companyNumber ?? '');
    setAnswers(Object.fromEntries(cfg.questions.map((q) => [q.key, q.value])));
    setPredicateQuestions(cfg.predicateQuestions);
    setPredicates(
      Object.fromEntries(
        cfg.predicateQuestions
          .filter((q) => typeof q.value === 'boolean')
          .map((q) => [q.key, q.value as boolean]),
      ),
    );
  }

  // Fetch the config on mount / when the active company changes.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setConfigLoading(true);
    setLoadError(null);
    getCompanyConfig(companyId)
      .then((cfg) => {
        if (cancelled) return;
        applyConfig(cfg);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(errorText(err, 'Could not load your company. Please try again.'));
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // The page hangs off the active company; wait for it before reading fields.
  if (!company) {
    const busy = loading || companyLoading;
    return (
      <div className="apage">
        <PageHeader eyebrow="Company" title="Your company" />
        <div className="acard">
          <div className="empty">
            {busy ? 'Loading your company…' : 'Finish onboarding to set up your company.'}
          </div>
        </div>
      </div>
    );
  }

  if (configLoading || !config) {
    return (
      <div className="apage">
        <PageHeader eyebrow="Company" title="Your company" />
        <div className="acard">
          <div className="empty">
            {loadError ?? 'Loading your company…'}
          </div>
        </div>
      </div>
    );
  }

  // A company's sector may pre-date the current sector list — keep it selectable.
  const sectorOptions = SECTORS.includes(sector) ? SECTORS : [sector, ...SECTORS];

  const declaredNum = Math.max(0, Math.floor(Number(declared) || 0));
  const employeeHeadcount = config.employeeHeadcount;
  const effective = Math.max(declaredNum, employeeHeadcount);

  const setAnswer = (key: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  /**
   * Answer a predicate, then ask the server which questions apply now. An answer can unlock children
   * (yes) or orphan them (no), and the response is already sanitized, so the returned list and values
   * are exactly what a save would store.
   */
  function setPredicate(key: string, value: boolean) {
    const next = { ...predicates, [key]: value };
    setPredicates(next);
    if (!companyId) return;
    void previewCompanyQuestions(companyId, next)
      .then((res) => {
        setPredicateQuestions(res.questions);
        // Drop answers the server discarded (a child orphaned by a parent just set to no), so the
        // page never holds a value that would vanish on save.
        const kept = new Set(res.questions.map((q) => q.key));
        setPredicates((prev) =>
          Object.fromEntries(Object.entries(prev).filter(([k]) => kept.has(k))),
        );
      })
      .catch(() => {
        // A failed preview must not block editing; Save still validates against the server.
      });
  }

  const declaredValid = declared.trim() !== '' && Number.isFinite(Number(declared)) && declaredNum >= 0;
  const questionsAnswered = config.questions.every(
    (q) => (answers[q.key] ?? '').trim().length > 0,
  );
  // Every applicable predicate needs an explicit yes or no. An unanswered one is absent, which the
  // resolver reads as "no answer", pinning the obligations it gates at needs-answer with no way to
  // clear them (COMPLIANCE-MODEL.md §5, §6). So it blocks Save, exactly like the employee editor.
  const unansweredPredicates = predicateQuestions.filter(
    (q) => typeof predicates[q.key] !== 'boolean',
  );
  const canSave =
    name.trim().length > 0 &&
    declaredValid &&
    questionsAnswered &&
    unansweredPredicates.length === 0 &&
    !submitting;

  async function onSave() {
    if (submitLock.current || !companyId || !canSave) return;
    submitLock.current = true;
    setSubmitting(true);
    setSaveError(null);
    try {
      const updated = await updateCompany(companyId, {
        name: name.trim(),
        sector,
        region,
        declaredHeadcount: declaredNum,
        companyType,
        // Only registered types carry a number; clear it (send '') for the rest.
        companyNumber: isRegisteredCompanyType(companyType) ? companyNumber.trim() : '',
        answers,
        // Full replace. Safe to send because the questions are loaded on this page; omitting it would
        // mean "leave untouched" and sending {} would wipe every answer.
        predicates,
      });
      applyConfig(updated);
      // Refresh the portfolio so the sidebar chip's effective headcount updates.
      await refetchCompanies();
      showSaved();
    } catch (err) {
      setSaveError(errorText(err, 'Could not save your changes. Try again.'));
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  }

  return (
    <div className="apage">
      <PageHeader
        eyebrow="Company"
        title="Your company"
        lead={
          <>
            These details decide what your requirements are. Keep them current and <Brand /> keeps
            up.
          </>
        }
      />

      <div className="co-stack">
        {/* -------------------------------------------------- Company details */}
        <section className="acard acard--pad">
          <h2 className="acard__title">Company details</h2>
          <p className="acard__sub">The essentials Reeve uses to work out the right requirements.</p>

          <div className="co-form">
            <div className="field co-form__full">
              <label className="field__label" htmlFor="co-name">
                Company name
              </label>
              <input
                id="co-name"
                className="input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="co-sector">
                Sector
              </label>
              <select
                id="co-sector"
                className="select"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
              >
                {sectorOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="co-region">
                Jurisdiction
              </label>
              <select
                id="co-region"
                className="select"
                value={region}
                onChange={(e) => setRegion(e.target.value as CompanyRegion)}
              >
                {REGION_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <span className="field__hint">Some employment rules differ between the two.</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="co-type">
                Company type
              </label>
              <select
                id="co-type"
                className="select"
                value={companyType}
                onChange={(e) => setCompanyType(e.target.value as CompanyType)}
              >
                {COMPANY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {isRegisteredCompanyType(companyType) && (
              <div className="field">
                <label className="field__label" htmlFor="co-number">
                  Company number
                </label>
                <input
                  id="co-number"
                  className="input"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 09876543"
                  value={companyNumber}
                  onChange={(e) => setCompanyNumber(e.target.value)}
                />
                <span className="field__hint">Optional. From the Companies House register.</span>
              </div>
            )}
          </div>
        </section>

        {/* -------------------------------------------------------- Headcount */}
        <section className="acard acard--pad">
          <h2 className="acard__title">Headcount</h2>
          <p className="acard__sub">
            Your legal obligations change with how many people you employ, so Reeve needs this right.
          </p>

          <div className="co-form">
            <div className="field">
              <label className="field__label" htmlFor="co-declared">
                Employees you’ve declared
              </label>
              <input
                id="co-declared"
                className="input"
                type="number"
                inputMode="numeric"
                min={0}
                value={declared}
                onChange={(e) => setDeclared(e.target.value)}
              />
              <span className="field__hint">
                Count part-time, casual and zero-hours staff. Don’t include yourself or any unpaid
                directors.
              </span>
            </div>

            <div className="field">
              <span className="field__label">People on your team list</span>
              <div className="co-headcount">
                <span className="co-headcount__num">{employeeHeadcount}</span>
                <span className="co-headcount__note">
                  Counted from your employees. It updates as you add and remove people.
                </span>
              </div>
              <span className="field__hint">
                <Link to="/app/employees">Manage employees</Link>
              </span>
            </div>
          </div>

          <div className="co-effective">
            <span className="co-effective__label">Reeve uses</span>
            <span className="co-effective__num">{effective}</span>
            <p className="co-effective__note">
              the higher of the two, {effective === employeeHeadcount && employeeHeadcount > declaredNum
                ? 'your team list'
                : 'the number you declared'}
              . Your legal obligations change with how many people you employ, so Reeve always works
              from the larger number.
            </p>
          </div>
        </section>

        {/* ----------------------------------------------------- Your business */}
        {config.questions.length > 0 && (
          <section className="acard acard--pad">
            <h2 className="acard__title">Your business</h2>
            <p className="acard__sub">
              What Reeve knows about how you operate. Keep it accurate, it shapes your requirements.
            </p>

            <div className="co-form">
              {config.questions.map((q) => (
                <div className="field co-form__full" key={q.key}>
                  <span className="field__label">{q.prompt}</span>
                  {q.help && <span className="field__hint">{q.help}</span>}
                  {renderQuestion(q, answers[q.key] ?? '', (v) => setAnswer(q.key, v))}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ------------------------------------------- Your legal circumstances */}
        {predicateQuestions.length > 0 && (
          <section className="acard acard--pad">
            <h2 className="acard__title">Your legal circumstances</h2>
            <p className="acard__sub">
              These decide which rules apply to you. Change one and your requirements change with it.
              Answering yes can add a follow-up question.
            </p>

            <div className="co-form">
              {predicateQuestions.map((q) => {
                // Same derivation as onboarding: indentation comes from the ancestry chain, so a
                // follow-up always sits under whatever revealed it.
                const depth = questionDepth(q, predicateQuestions);
                return (
                <div
                  className={`field co-form__full${depth ? ' field--nested' : ''}`}
                  style={depth ? ({ '--q-depth': depth } as CSSProperties) : undefined}
                  key={q.key}
                >
                  <span className="field__label">{q.prompt}</span>
                  {q.help && <span className="field__hint">{q.help}</span>}
                  <div className="co-toggle" role="group" aria-label={q.prompt}>
                    <button
                      type="button"
                      className={`co-toggle__btn${predicates[q.key] === true ? ' is-on' : ''}`}
                      aria-pressed={predicates[q.key] === true}
                      onClick={() => setPredicate(q.key, true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`co-toggle__btn${predicates[q.key] === false ? ' is-on' : ''}`}
                      aria-pressed={predicates[q.key] === false}
                      onClick={() => setPredicate(q.key, false)}
                    >
                      No
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------- Save */}
        <div className="acard acard--pad">
          {saveError && (
            <p className="co-ob__error" role="alert">
              {saveError}
            </p>
          )}
          <div className="co-cardfoot">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!canSave}
              onClick={() => void onSave()}
            >
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
            {saved && (
              <span className="co-saved" role="status">
                <Tick size={14} />
                Saved
              </span>
            )}
            {(!questionsAnswered || unansweredPredicates.length > 0) && (
              <span className="field__hint">
                {unansweredPredicates.length > 0
                  ? `Answer the remaining ${unansweredPredicates.length} question${
                      unansweredPredicates.length === 1 ? '' : 's'
                    } before saving.`
                  : 'Answer every question before saving.'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Render the input for a findings question by its `kind` (mirrors onboarding). */
function renderQuestion(
  question: OnboardingQuestionWithValue,
  value: string,
  onChange: (v: string) => void,
): ReactNode {
  switch (question.kind) {
    case 'longText':
      return (
        <textarea
          className="input"
          rows={4}
          style={{ resize: 'vertical', minHeight: 112 }}
          placeholder="Type your answer…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return (
        <input
          className="input"
          type="number"
          inputMode="numeric"
          placeholder="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'boolean':
      return (
        <div className="co-toggle" role="group" aria-label={question.prompt}>
          <button
            type="button"
            className={`co-toggle__btn${value === 'true' ? ' is-on' : ''}`}
            aria-pressed={value === 'true'}
            onClick={() => onChange('true')}
          >
            Yes
          </button>
          <button
            type="button"
            className={`co-toggle__btn${value === 'false' ? ' is-on' : ''}`}
            aria-pressed={value === 'false'}
            onClick={() => onChange('false')}
          >
            No
          </button>
        </div>
      );
    case 'choice':
      return (
        <div className="co-choices">
          {(question.options ?? []).map((opt) => {
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`co-choice${selected ? ' is-selected' : ''}`}
                aria-pressed={selected}
                onClick={() => onChange(opt.value)}
              >
                <span className="co-choice__radio" aria-hidden="true">
                  {selected && <Tick size={12} />}
                </span>
                <span>
                  <span className="co-choice__name">{opt.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      );
    case 'text':
    default:
      return (
        <input
          className="input"
          type="text"
          placeholder="Type your answer…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
